/**
 * CHESTERMERE-AREA — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 9 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_CHESTERMERE_AREA: Location[] = [
// ─────────────────────────────────────────────────────────────── CHELSEA ──
  {
    slug: "chelsea",
    name: "Chelsea",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.0325, lng: -113.8395 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 16,
      authorizationRequired: true,
      authorizationNote:
        "About 1 km from Chestermere and in the same airspace corridor, Chelsea is planned as controlled airspace. Operations run under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site calls for one. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Chelsea's roads are finished before its streetscape is — from the air, Chelsea Boulevard and the streets branching from it read as a complete, curbed network laid over ground the houses have not caught up to yet.",
      consequence:
        "A ground-level photograph of a new build here frames bare lots, hoarding and unplanted boulevards — the buyer sees a construction site, not the community the plan promises or how close it sits to Chestermere Lake.",
      resolution:
        "An elevated frame down the line of Chelsea Boulevard shows the finished network, the build edge and the lake in one composition, and a repeat progress series flown to the same waypoints tracks the community filling in — delivered in the standard turnaround.",
    },
    landmarks: [
      "Chelsea Boulevard",
      "West Chestermere Drive",
      "Chestermere Lake",
      "Chestermere Recreation Centre",
      "Chelsea Ridge Park",
    ],
    terrain:
      "Flat prairie at Chestermere's western edge, mid-conversion from cultivated ground to subdivision — new road network and graded lots on one side of the frame, open field on the other.",
    windNote:
      "Chinook belt with nothing upwind. West wind arrives off open prairie unbroken, a building site offers no shelter, and bare graded ground throws dust in a dry blow.",
    seasonalWindow:
      "Green-up flatters a part-built community: sod and open field read green together and the raw edges soften. Snow erases the difference between finished yards and bare lots, which suits progress documentation better than sales frames.",
    bylawNote:
      "City of Chestermere park and pathway rules govern takeoff and landing on municipal land, including Chelsea Ridge Park; launches are staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes east of the Calgary base. Same-day is realistic once the authorisation position is confirmed, and Westmere or West Creek pair naturally on the same trip.",
    faqs: [
      {
        question: "Do drone flights in Chelsea need NAV CANADA authorisation?",
        answer:
          "Often, yes. Chelsea sits on the outer edge of the Calgary International (CYYC) control zone, about 16 km from the airport, so flights here are planned as controlled airspace. Fly4MEdia flies under an Advanced RPAS certificate, files a NAV CANADA authorisation wherever the site requires one, and confirms every address against NAV Drone first.",
      },
      {
        question: "Can you document a build in Chelsea over several months?",
        answer:
          "Yes. A progress series is flown to the same waypoints, altitudes and headings on each visit, so the frames align and the change is the only thing that moves. The 25-minute drive from Calgary keeps repeat visits practical to schedule around weather and light.",
      },
      {
        question: "What does aerial photography add to a new-build listing in Chelsea?",
        answer:
          "Context the curb cannot show. In a community still building out, the aerial places a finished home within the road network, shows the open prairie behind it, and puts Chestermere Lake and the Chestermere Recreation Centre in frame — the amenities a ground photograph has to leave out.",
      },
    ],
    primaryKeywords: [
      "drone photography Chelsea",
      "aerial photography Chelsea",
      "Chelsea drone services",
    ],
    nearestLocations: ["chestermere", "kinniburgh", "westmere", "west-creek"],
  },

  // ────────────────────────────────────────────────────── DAWSON'S LANDING ──
  {
    slug: "dawsons-landing",
    name: "Dawson's Landing",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.0245, lng: -113.8225 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 17,
      authorizationRequired: true,
      authorizationNote:
        "About 2 km from Chestermere and in the same airspace corridor, Dawson's Landing is planned as controlled airspace. Operations run under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site calls for one. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The Chestermere Lake Canals are working irrigation infrastructure, and their water keeps farm hours — full and reflective through the irrigation season, drawn down to a low channel once it ends.",
      consequence:
        "A canal-side listing shot in the wrong month trades its water feature for an empty channel, and the frame meant to sell waterside living reads as a ditch behind a fence.",
      resolution:
        "The shoot is scheduled against the canal's season rather than the calendar's — a golden-hour hold while the channel is full puts lit water beside the property, delivered in the standard turnaround.",
    },
    landmarks: [
      "Chestermere Lake Canals",
      "Dawson's Landing Park",
      "Chestermere Golf Club",
      "Dawson's Wharf",
      "South Shore Circle",
    ],
    terrain:
      "Flat prairie at the south end of Chestermere, threaded by the canal system that feeds and drains the lake — the water lines are the only geometry the survey grid does not control.",
    windNote:
      "Chinook belt, flat and open. There is no ground here tall enough to slow a west wind, and the lake and canal surfaces show the gusts as moving cat's-paws before an anemometer does.",
    seasonalWindow:
      "Two clocks run here: the lake reads as water from ice-off to freeze-up, and the canals hold water for the shorter irrigation season inside that. The full-water overlap in high summer is when the neighbourhood photographs as it is sold.",
    bylawNote:
      "Launch and landing on City of Chestermere land — parks, pathways and the canal reserve — follow the city's rules; the default is takeoff from private property with the client's consent.",
    subjectMix: ["lakefront", "luxury-residential", "new-build-subdivision"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. An easy same-day window when weather and authorisation line up, and The Cove or East Chestermere can share the mobilisation.",
    faqs: [
      {
        question: "What airspace covers Dawson's Landing?",
        answer:
          "The outer edge of the Calgary International (CYYC) control zone, with the airport about 17 km away. Flights here are planned as controlled airspace: Fly4MEdia operates under an Advanced RPAS certificate, obtains a NAV CANADA authorisation wherever the specific site requires one, and checks the site and altitude in NAV Drone before every flight.",
      },
      {
        question: "When should a canal-side property in Dawson's Landing be photographed?",
        answer:
          "While the canals are carrying water. The Chestermere Lake Canals are irrigation infrastructure, so the channel that reflects the sky in summer can sit drawn down outside the season. The booking is timed to full water, and a golden-hour slot doubles the effect off the surface.",
      },
      {
        question: "Can the golf course appear in an aerial frame here?",
        answer:
          "Yes. Chestermere Golf Club is close enough to hold in frame from altitude, and its fairways supply the largest block of green in the composition. An orbit can keep a property, the course and the lake in a single frame — a relationship no ground-level photograph can establish.",
      },
    ],
    primaryKeywords: [
      "drone photography Dawson's Landing",
      "aerial photography Dawson's Landing",
      "Dawson's Landing drone services",
    ],
    nearestLocations: ["chestermere", "the-cove", "lakeside-greens", "east-chestermere"],
  },

  // ────────────────────────────────────────────────────── EAST CHESTERMERE ──
  {
    slug: "east-chestermere",
    name: "East Chestermere",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.0385, lng: -113.7995 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 18,
      authorizationRequired: true,
      authorizationNote:
        "About 1 km from Chestermere and in the same airspace corridor, East Chestermere is planned as controlled airspace. Operations run under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site calls for one. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "A west wind reaching East Chestermere has just crossed the full open width of Chestermere Lake, so this shoreline takes it unbroken — the chop arrives as evidence before any forecast does.",
      consequence:
        "Dock and marina frames on this shore inherit the roughest water state on the lake; shot on the wrong afternoon, a listing built on calm waterfront shows whitecaps against the boat lift.",
      resolution:
        "The flight is planned for the calm end of the day — typically morning, before the wind builds — with an orbit off East Chestermere Marina putting still water, frontage and the property in one frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "East Chestermere Drive",
      "East Chestermere Marina",
      "Chestermere Lake",
      "Anniversary Park",
      "East Lakeview Road",
    ],
    terrain:
      "The lake's eastern shore: flat prairie meeting the reservoir in a line of private docks and mature yards, with open grain land running east behind the houses.",
    windNote:
      "Chinook belt, downwind shore. Westerlies cross the water without obstruction and arrive carrying the lake's full fetch; surface chop here is the most honest wind report in Chestermere.",
    seasonalWindow:
      "The shore reads best between ice-off and freeze-up, when water, docks and boats make the argument. Evening sun faces this shoreline and lights the docks; mornings bring the calmer water — the lake rarely offers both at once.",
    bylawNote:
      "City of Chestermere rules govern launch and landing on municipal land, the pathway system and the shoreline reserve; takeoff is staged from private property with the client's consent.",
    subjectMix: ["lakefront", "luxury-residential"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. Same-day work is realistic, and Lakeside Greens or Dawson's Landing can be flown on the same trip when conditions hold.",
    faqs: [
      {
        question: "Is East Chestermere inside the Calgary airport's control zone?",
        answer:
          "It sits on the zone's outer edge, about 18 km from Calgary International, and flights here are planned as controlled airspace. Fly4MEdia operates under an Advanced RPAS certificate, files a NAV CANADA authorisation where the specific site requires one, and verifies the address against NAV Drone before every flight.",
      },
      {
        question: "When is the water calmest for a lakefront shoot in East Chestermere?",
        answer:
          "Usually morning. This is the downwind shore for the prevailing westerlies, so afternoon chop lands here first and hardest. Booking the early window trades front light for still water — the trade that usually wins for dock and frontage frames — with a backup slot held for wind.",
      },
      {
        question: "Can a boat and dock be photographed as part of the listing?",
        answer:
          "Yes — frontage is the point on East Chestermere Drive. An orbit from over the water holds the house, dock and moored boat in one frame, and East Chestermere Marina gives context frames of the wider shoreline. Water-side angles like these are exactly what ground photography cannot reach.",
      },
    ],
    primaryKeywords: [
      "drone photography East Chestermere",
      "aerial photography East Chestermere",
      "East Chestermere drone services",
    ],
    nearestLocations: ["chestermere", "lakeside-greens", "the-cove", "dawsons-landing"],
  },

  // ──────────────────────────────────────────────────────────── KINNIBURGH ──
  {
    slug: "kinniburgh",
    name: "Kinniburgh",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.0455, lng: -113.8331 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 15,
      authorizationRequired: true,
      authorizationNote:
        "Kinniburgh shares its airspace corridor with Chestermere, about 1 km away, and flights through it are planned as controlled airspace. An Advanced RPAS certificate covers the work, with NAV CANADA RPAS authorisation obtained wherever the specific site requires it. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Kinniburgh is the Chestermere neighbourhood organised around a campus rather than the shoreline — Chestermere High School and Kinniburgh Sports Park anchor its centre, and the streets are laid out around them.",
      consequence:
        "The family buyer's question here is walking distance, and a ground-level photograph cannot answer it — the school and the sports park are both invisible from the curb of the house being sold.",
      resolution:
        "One elevated frame holds the home, Chestermere High School and Kinniburgh Sports Park in a single composition, turning proximity from a line in the listing into something the buyer can see — delivered in the standard turnaround.",
    },
    landmarks: [
      "Kinniburgh Boulevard",
      "Kinniburgh Sports Park",
      "Chestermere High School",
      "Chestermere Lake",
    ],
    terrain:
      "Flat prairie carrying one of Chestermere's newer street grids — large-lot residential blocks arranged around the school and sports-park quarter, with the lake a short drive away.",
    windNote:
      "Chinook belt. Away from the water there is no fetch to read, so conditions are taken from forecast winds aloft rather than the lake surface; the open prairie edge gives gusts a clean run into the neighbourhood.",
    seasonalWindow:
      "A young streetscape means little tree canopy to hide rooflines — winter and summer frames differ mainly in ground colour. The sports park reads best in the green months, when its fields separate cleanly from the surrounding blocks.",
    bylawNote:
      "Takeoff and landing on City of Chestermere land, including Kinniburgh Sports Park, are governed by the city's park rules; launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. Comfortably same-day, and Westmere or Rainbow Falls can be added to the same mobilisation.",
    faqs: [
      {
        question: "Can a drone legally fly over Kinniburgh?",
        answer:
          "Yes, with the right credentials. Kinniburgh sits on the outer edge of the Calgary International control zone, about 15 km from the airport, so work is planned as controlled airspace under an Advanced RPAS certificate. A NAV CANADA authorisation is obtained where the site requires one, and NAV Drone is checked before every flight.",
      },
      {
        question: "Do you photograph near Chestermere High School?",
        answer:
          "Yes, with placement planned around it. School grounds are not used for launch, and flight paths keep clear separation from the property. For family-market listings the school appears as context in an elevated frame — which is usually the point, since walk-to-school distance is Kinniburgh's strongest selling line.",
      },
    ],
    primaryKeywords: [
      "drone photography Kinniburgh",
      "aerial photography Kinniburgh",
      "Kinniburgh drone services",
    ],
    nearestLocations: ["chestermere", "westmere", "rainbow-falls"],
  },

  // ─────────────────────────────────────────────────────── LAKESIDE GREENS ──
  {
    slug: "lakeside-greens",
    name: "Lakeside Greens",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.0405, lng: -113.8155 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 17,
      authorizationRequired: true,
      authorizationNote:
        "Lakeside Greens sits in the same airspace corridor as Chestermere, about 0 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Almost the only sculpted ground in Chestermere belongs to Lakeside Greens Golf Course — its mounds, swales and water hazards are relief that was built, not inherited, in a town that is otherwise level.",
      consequence:
        "Flat ground dies under high sun: shot at midday, a fairway-backing home loses the contours behind it and the course reads as plain lawn, taking the property's premium out of the frame.",
      resolution:
        "A low-sun orbit rakes light across the shaped ground so the mounding throws shadow and the hazards read as water — a golden-hour hold that puts the course's geometry behind the house, delivered in the standard turnaround.",
    },
    landmarks: [
      "Lakeside Greens Golf Course",
      "Lakeside Greens Drive",
      "Chestermere Lake",
      "Lakeside Community Park",
    ],
    terrain:
      "Level prairie beside the lake, interrupted by the one landform Chestermere built for itself — the shaped fairways, ponds and mounding of the golf course threading between residential streets.",
    windNote:
      "Chinook belt. The course's mounding is scenery, not shelter — built relief this low does nothing to a west wind, and the water hazards ripple with gusts before they reach the pin flags.",
    seasonalWindow:
      "The course sets the calendar: from spring green-up to the first lasting snow, the fairways give these listings their colour and depth. Under snow the built relief flattens to white and the premium view goes with it.",
    bylawNote:
      "Lakeside Greens Golf Course is private property, and the city's park and pathway rules govern the municipal land around it, so launches are arranged from the client's property or with the relevant owner's consent.",
    subjectMix: ["luxury-residential", "lakefront"],
    mobilisationNote:
      "About 25 minutes from the Calgary base — same-day work is realistic. East Chestermere and Dawson's Landing sit close enough to share the trip.",
    faqs: [
      {
        question: "Does a shoot in Lakeside Greens need airspace permission?",
        answer:
          "Frequently, yes. The neighbourhood lies on the outer edge of the Calgary International (CYYC) control zone, about 17 km from the airport, and flights are planned as controlled airspace. Fly4MEdia holds an Advanced RPAS certificate, secures NAV CANADA authorisation wherever the site requires it, and confirms every site in NAV Drone first.",
      },
      {
        question: "What is the best time of day to photograph a golf-course-backing home here?",
        answer:
          "The last hours of light. The course's mounding and hazards are the only relief in the frame, and only low sun makes them read — midday flattens the shaped ground into plain lawn. Golden hour also puts warm light on the water hazards and the lake beyond.",
      },
      {
        question: "Can you fly over the golf course itself?",
        answer:
          "Flight paths are planned with the course, not simply over it. Launch happens from the client's property, orbits are positioned to keep clear of play, and the fairways enter the frame as backdrop. The course's geometry is the value; the flight is arranged so it appears without disrupting anyone below.",
      },
    ],
    primaryKeywords: [
      "drone photography Lakeside Greens",
      "aerial photography Lakeside Greens",
      "Lakeside Greens drone services",
    ],
    nearestLocations: ["chestermere", "east-chestermere", "west-creek", "dawsons-landing"],
  },

  // ───────────────────────────────────────────────────────── RAINBOW FALLS ──
  {
    slug: "rainbow-falls",
    name: "Rainbow Falls",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.033, lng: -113.8145 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 17,
      authorizationRequired: true,
      authorizationNote:
        "About 1 km from Chestermere and in the same airspace corridor, Rainbow Falls is planned as controlled airspace. Operations run under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site calls for one. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Rainbow Falls sits about 17 km from the Calgary International runways, on the outer edge of the CYYC control zone — close enough that the charted airspace boundary, not the neighbourhood boundary, decides how a flight here is planned.",
      consequence:
        "An operator who treats Chestermere as outside Calgary and skips the check either flies illegally or cancels on arrival — and either way the client carries the risk on shoot day.",
      resolution:
        "The airspace position is resolved at booking: Advanced RPAS certification, a NAV CANADA authorisation filed wherever the site requires one, and a NAV Drone confirmation per address — so the date that is confirmed is a date that can be flown, in the standard turnaround.",
    },
    landmarks: [
      "Rainbow Falls Drive",
      "Rainbow Falls School",
      "Anniversary Park",
      "Chestermere Lake",
      "Rainbow Falls Boulevard",
    ],
    terrain:
      "Flat lakeside prairie carrying one of Chestermere's established family street grids, with Anniversary Park and the lake supplying the open ground the neighbourhood composes against.",
    windNote:
      "Chinook belt, level and open. A west wind crosses the town with nothing to slow it, so flight windows are chosen on the gust forecast rather than the average.",
    seasonalWindow:
      "Between ice-off and freeze-up the lake anchors wide frames from this side of town; in winter the open-water argument disappears and compositions tighten to streetscape and rooflines against snow.",
    bylawNote:
      "City of Chestermere park bylaws govern takeoff and landing in parks and on pathways, Anniversary Park included; the working default is launch from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. The authorisation work happens at booking, not on site, so same-day remains realistic; Westmere and Kinniburgh pair well on the same trip.",
    faqs: [
      {
        question: "How does Calgary's control zone affect drone flights in Rainbow Falls?",
        answer:
          "It sets the paperwork. Rainbow Falls lies on the CYYC control zone's outer edge, about 17 km from the airport, so flights are planned as controlled airspace. Fly4MEdia flies under an Advanced RPAS certificate and files a NAV CANADA authorisation where the site requires one, confirmed against NAV Drone.",
      },
      {
        question: "How far ahead should a Rainbow Falls shoot be booked?",
        answer:
          "Far enough to absorb one weather day. The airspace authorisation is handled as part of booking rather than on site, so what moves a Rainbow Falls date is wind or cloud, not paperwork. The 25-minute mobilisation from Calgary makes a rebooked slot easy to fill.",
      },
      {
        question: "Can Anniversary Park and the lake appear behind a Rainbow Falls listing?",
        answer:
          "Yes. From altitude the neighbourhood's position beside the lake is its clearest advantage, and a frame angled across Anniversary Park toward open water places the home in that context. Between ice-off and freeze-up the water reads blue; in winter the composition shifts to streetscape.",
      },
    ],
    primaryKeywords: [
      "drone photography Rainbow Falls",
      "aerial photography Rainbow Falls",
      "Rainbow Falls drone services",
    ],
    nearestLocations: ["chestermere", "westmere", "kinniburgh"],
  },

  // ────────────────────────────────────────────────────────────── THE COVE ──
  {
    slug: "the-cove",
    name: "The Cove",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.0295, lng: -113.8135 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 17,
      authorizationRequired: true,
      authorizationNote:
        "The Cove sits in the same airspace corridor as Chestermere, about 1 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Alberta street suffixes are a map key — a Bay or a Point is a closed street — and The Cove is written almost entirely in them: Cove Bay, Cove Point and Rocky Ridge Bay all end at a turning circle instead of connecting through.",
      consequence:
        "The quiet that cul-de-sacs sell is invisible from inside one — a ground photograph of a turning circle is pavement, and it cannot show the pattern that creates the quiet or how close the water sits behind it.",
      resolution:
        "An orbit above the turning circle reads the whole pattern at once — the closed street, the surrounding network, the Chestermere Regional Pathway and the lake — and hands the listing its setting in one frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Cove Bay",
      "Cove Point",
      "Rocky Ridge Bay",
      "The Cove Boat Launch",
      "Chestermere Regional Pathway",
    ],
    terrain:
      "Flat lakeshore prairie subdivided into short closed streets that back toward the water, with the pathway system and the boat launch stitching the shoreline together.",
    windNote:
      "Chinook belt on an open shore. Housing this low breaks no wind, and the water off The Cove Boat Launch shows direction and strength before any instrument does.",
    seasonalWindow:
      "Open water makes this neighbourhood's case, so ice-off to freeze-up is the season that sells it. The boat launch is busiest in high summer, which is also when golden-hour water carries the most colour.",
    bylawNote:
      "The Cove Boat Launch, the Chestermere Regional Pathway and the city's parks fall under City of Chestermere rules for takeoff and landing, so launches are staged from private property with the client's consent.",
    subjectMix: ["lakefront", "luxury-residential"],
    mobilisationNote:
      "About 25 minutes from the Calgary base — an easy same-day window. West Creek and East Chestermere sit within the same mobilisation.",
    faqs: [
      {
        question: "Are drones allowed to fly in The Cove?",
        answer:
          "Yes, under controlled-airspace planning. The Cove sits on the outer edge of the Calgary International control zone, about 17 km from the airport. Fly4MEdia operates here under an Advanced RPAS certificate, obtains NAV CANADA authorisation where the specific site requires it, and confirms each address in NAV Drone before flying.",
      },
      {
        question: "What can an aerial show about a cul-de-sac property that ground photography cannot?",
        answer:
          "The pattern. Streets like Cove Bay and Cove Point end at turning circles, and the quiet they sell only appears when the whole layout is visible — the closed street, the neighbours, the pathway and the lake behind. One orbit at altitude reads all of it in a single frame.",
      },
      {
        question: "Can the boat launch feature in a listing shoot?",
        answer:
          "Yes. The Cove Boat Launch is the neighbourhood's direct line to the water, and an elevated frame can hold a property and the launch in one composition. For buyers moving here for the lake, that distance-to-water shot usually works harder than any interior photograph.",
      },
    ],
    primaryKeywords: [
      "drone photography The Cove",
      "aerial photography The Cove",
      "The Cove drone services",
    ],
    nearestLocations: ["chestermere", "west-creek", "east-chestermere", "lakeside-greens"],
  },

  // ──────────────────────────────────────────────────────────── WEST CREEK ──
  {
    slug: "west-creek",
    name: "West Creek",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.0295, lng: -113.8465 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 16,
      authorizationRequired: true,
      authorizationNote:
        "West Creek sits in the same airspace corridor as Chestermere, about 2 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "West Creek's centrepiece photographs by borrowed light — at low sun, West Creek Pond stops being a dark patch on flat ground and turns into a mirror, briefly the brightest surface in the neighbourhood.",
      consequence:
        "Shot at midday, the same pond reads grey and the feature the streets are named for disappears from the frame; a schedule built on convenience rather than sun angle gives the listing its dullest possible version.",
      resolution:
        "The flight is timed to the low-sun window and held there — a golden-hour hold over West Creek Pond puts lit water in the foreground and Chestermere Lake on the horizon, delivered in the standard turnaround.",
    },
    landmarks: [
      "West Creek Pond",
      "West Creek Drive",
      "West Creek Community Park",
      "Chestermere Lake",
      "West Chestermere Drive",
    ],
    terrain:
      "Level prairie on Chestermere's west side, organised around a constructed pond and its green edge, with the lake a short distance east and open country running west toward Calgary.",
    windNote:
      "Chinook belt with a western exposure. Wind off the open prairie arrives here with nothing spent, and the pond's small surface wrinkles at gusts the forecast averages hide.",
    seasonalWindow:
      "The flat western horizon gives West Creek the full golden hour the almanac promises — no foothill or valley wall cuts it short. The pond holds its mirror season from ice-off to freeze-up, and green-up ties its edge back into West Creek Community Park.",
    bylawNote:
      "City of Chestermere bylaws cover takeoff and landing in parks and on pathways, West Creek Community Park included; the standing default is launch from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential"],
    mobilisationNote:
      "About 25 minutes from the Calgary base, on the near side of Chestermere. Chelsea and Westmere pair readily on the same trip for the same light window.",
    faqs: [
      {
        question: "What are the airspace rules for a drone shoot in West Creek?",
        answer:
          "West Creek sits on the outer edge of the Calgary International control zone, about 16 km from the airport, so flights are planned as controlled airspace. Fly4MEdia holds an Advanced RPAS certificate, files NAV CANADA authorisation wherever the specific site requires it, and checks every address against NAV Drone before flight.",
      },
      {
        question: "When does West Creek Pond look best from the air?",
        answer:
          "In the low-sun hours. The pond is the neighbourhood's defining feature, and it only reads as water when the light angle turns it reflective — at midday it goes flat and grey. Booking the golden-hour window puts a lit mirror in the frame instead of a dark patch.",
      },
    ],
    primaryKeywords: [
      "drone photography West Creek",
      "aerial photography West Creek",
      "West Creek drone services",
    ],
    nearestLocations: ["chestermere", "chelsea", "westmere", "east-chestermere"],
  },

  // ────────────────────────────────────────────────────────────── WESTMERE ──
  {
    slug: "westmere",
    name: "Westmere",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.0505, lng: -113.828 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 15,
      authorizationRequired: true,
      authorizationNote:
        "Westmere sits in the same airspace corridor as Chestermere, about 1 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "In a west wind the water off Westmere is the flattest on Chestermere Lake — waves need distance to build, and the upwind shore gives them none.",
      consequence:
        "That calm is a trap for judgement: a pilot reading conditions off the smooth water launches into wind aloft the surface never showed, and the footage pays for it in horizon wobble and cut-short sets.",
      resolution:
        "Conditions are planned from forecast winds aloft rather than the look of the lake, with the gust spread setting the window — so the orbit over the shoreline runs its full set, delivered in the standard turnaround.",
    },
    landmarks: [
      "Westmere Drive",
      "Westmere School",
      "Chestermere Recreation Centre",
      "Chestermere Lake",
    ],
    terrain:
      "Flat prairie along the lake's western side, built out as one of Chestermere's principal residential quarters, with the Chestermere Recreation Centre grounds supplying its largest open block.",
    windNote:
      "Chinook belt. The lake lies downwind of this shore in a westerly, so Westmere's water stays deceptively smooth while the far shore takes the chop — surface appearance here understates what is moving aloft.",
    seasonalWindow:
      "Ice-off to freeze-up is the season when lake frames carry the neighbourhood's case. The recreation centre grounds and the fields at Westmere School hold their green through summer and read as the district's open space in wide shots.",
    bylawNote:
      "Parks, pathways, school grounds and recreation land in Westmere fall under City of Chestermere takeoff and landing rules; launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "lakefront"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. Same-day scheduling is realistic, and Kinniburgh or Rainbow Falls can be covered on the same mobilisation.",
    faqs: [
      {
        question: "Is airspace authorisation needed to fly a drone in Westmere?",
        answer:
          "Usually, yes. Westmere lies on the outer edge of the Calgary International control zone, about 15 km from the airport, and flights are planned as controlled airspace. Fly4MEdia operates under an Advanced RPAS certificate, obtains a NAV CANADA authorisation where the site requires one, and confirms each site in NAV Drone.",
      },
      {
        question: "Why does calm water at the Westmere shoreline not guarantee a calm flight?",
        answer:
          "Because this is the upwind shore. In a westerly the waves build away from Westmere, so the water here stays smooth while stronger wind moves aloft. Flight windows are therefore set from the winds-aloft forecast and the gust spread, not from how the lake looks at launch.",
      },
      {
        question: "Can one shoot cover a Westmere home and the recreation centre context?",
        answer:
          "Yes. The Chestermere Recreation Centre is one of the largest open blocks on this side of the lake, and an elevated frame can hold a listing, the centre's grounds and the water in a single composition — the amenity picture a family buyer is actually weighing.",
      },
    ],
    primaryKeywords: [
      "drone photography Westmere",
      "aerial photography Westmere",
      "Westmere drone services",
    ],
    nearestLocations: ["chestermere", "kinniburgh", "rainbow-falls"],
  }
];
